import React, { useState, useEffect } from 'react';
import { Calculator, Volume2, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const ShareOfVoiceCalculator = () => {
  const [brandMentions, setBrandMentions] = useState(1000);
  const [totalIndustryMentions, setTotalIndustryMentions] = useState(10000);
  const [shareOfVoice, setShareOfVoice] = useState(0);

  useEffect(() => {
    calculateShareOfVoice();
  }, [brandMentions, totalIndustryMentions]);

  const calculateShareOfVoice = () => {
    const calculatedSov = (brandMentions / totalIndustryMentions) * 100;
    setShareOfVoice(parseFloat(calculatedSov.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/share-of-voice-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Share of Voice Calculator: Measure Brand Presence | Free Tool</title>
        <meta name="description" content="Calculate your Share of Voice instantly. Compare with industry benchmarks, get optimization tips, and improve your brand visibility." />
        <meta name="keywords" content="share of voice calculator, brand visibility metrics, market presence, SOV measurement, brand awareness" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Share of Voice Calculator: Optimize Brand Presence" />
        <meta property="og:description" content="Calculate your Share of Voice instantly. Get actionable insights to improve brand visibility and market presence." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Share of Voice Calculator: Optimize Brand Presence" />
        <meta property="twitter:description" content="Calculate your Share of Voice instantly. Get actionable insights to improve brand visibility and market presence." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Share of Voice Calculator",
            "description": "Free calculator to measure and optimize brand visibility and market presence",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Share of Voice",
              "Industry Benchmarks",
              "Visibility Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Share of Voice",
              "description": "The percentage of market presence and brand visibility compared to total industry mentions."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Brand Managers, Marketing Directors, PR Professionals"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Share of Voice",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter your brand mentions"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input total industry mentions"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your SOV and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Share of Voice Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Your Brand Mentions</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Volume2 className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={brandMentions}
                  onChange={(e) => setBrandMentions(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Industry Mentions</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Volume2 className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalIndustryMentions}
                  onChange={(e) => setTotalIndustryMentions(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {shareOfVoice}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Share of Voice</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Share of Voice is {shareOfVoice}%. This means that out of all the mentions in your industry, your brand accounts for {shareOfVoice}% of them.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry SOV Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Market Leaders: 40-50%</li>
          <li>Established Brands: 20-30%</li>
          <li>Growing Brands: 10-20%</li>
          <li>New Entrants: 5-10%</li>
          <li>Niche Players: 1-5%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">SOV Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Create shareable content</li>
          <li>Engage on social media</li>
          <li>Implement PR strategy</li>
          <li>Partner with influencers</li>
          <li>Attend industry events</li>
          <li>Optimize for search</li>
          <li>Generate reviews</li>
          <li>Monitor mentions</li>
          <li>Create thought leadership</li>
          <li>Leverage partnerships</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Channel Distribution</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Social Media: 35-45%</li>
          <li>News/PR: 20-30%</li>
          <li>Blogs: 15-25%</li>
          <li>Forums: 10-15%</li>
          <li>Reviews: 5-10%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>10% SOV increase = 0.7% market share gain</li>
          <li>PR campaigns boost SOV by 15-25%</li>
          <li>Influencer partnerships lift SOV by 20-30%</li>
          <li>Content marketing raises SOV by 10-20%</li>
          <li>Events increase SOV by 25-35%</li>
        </ul>
      </div>
    </div>
  );
};

export default ShareOfVoiceCalculator;
